import React, { forwardRef, useEffect, type RefObject } from 'react';
import InputContainer from '../../atom/InputContainer';
import useInputValue from '~/hooks/useInputValue';
import InputLabel from '../../atom/InputLabel';
import InputElement from '~/components/atom/InputElement';
import type { BaseInputProps } from '~/components/atom/InputElement';
import Icon, { type IconType } from '~/components/atom/Icon';
import type { ValidationFunction } from '~/util/getValidationErrors';
import Div from '~/components/atom/Div';
import type { InputAppearance } from '~/styles/constants';
import type { ThemeColor } from '~/theme/System/tokens/colorPalette';

export type Props = BaseInputProps & {
  dataTestId?: string;

  /** Input label. Becomes an error label if there is a validation error */
  label?: { text?: React.ReactNode; color?: ThemeColor };

  /** Input appearance */
  appearance?: InputAppearance;

  /** Width of the container */
  width?: string;

  /** Internal validation */
  validation?: Array<ValidationFunction>;

  /** External validation */
  externalErrors?: Array<string>;

  /** Shows a spinner on the right side */
  loading?: boolean;

  /** Icon to render on the left side of the input element */
  icon?: { name: IconType; color?: ThemeColor };

  /** Ref for the input container. Used to align a dropdown list by the container */
  inputContainerRef?: RefObject<HTMLDivElement>;
};

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      dataTestId,
      size = 'medium',
      disabled = false,
      label,
      value,
      validation = [],
      appearance,
      className,
      externalErrors = [],
      children,
      width,
      loading,
      icon,
      inputContainerRef,
      onChange,
      onFocus,
      onBlur,
      required,
      ...rest
    },
    ref,
  ) => {
    const {
      validationErrors,
      hasError,
      hasFocus,
      inputRef,
      onValueChange,
      onFocusChange,
    } = useInputValue({
      value,
      externalErrors,
      validation,
      type: rest.type,
      ref,
      onChange,
      onFocus,
      onBlur,
    });

    useEffect(() => {
      if (rest.autoFocus && inputRef.current) {
        // Allow to focus the input element inside a portal
        setTimeout(() => inputRef.current.focus(), 0);
      }
    }, [inputRef, rest.autoFocus]);

    return (
      <Div width={width}>
        <InputLabel
          size={size}
          error={validationErrors?.[0]}
          label={label?.text}
          required={required}
          color={label?.color}
        />
        <InputContainer
          size={size}
          hasError={hasError}
          hasFocus={hasFocus}
          width={width}
          appearance={appearance}
          className={className}
          disabled={disabled}
          inputRef={inputRef}
          ref={inputContainerRef}
        >
          {icon && (
            <Icon name={icon.name} color={icon.color} strokeWidth={2.5} />
          )}
          <InputElement
            {...rest}
            value={value}
            dataTestId={dataTestId}
            ref={inputRef}
            size={size}
            disabled={disabled}
            onChange={onValueChange}
            onFocus={e => onFocusChange({ e, focus: true })}
            onBlur={e => onFocusChange({ e, focus: false })}
          />
          {children}
          {loading && <Icon name="spinner" color={{ group: 'tertiary' }} />}
        </InputContainer>
      </Div>
    );
  },
);
export default Input;
